import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type {
  FlaggedContent,
  FlaggedContentableType,
} from "@circle-react/types";
import { isEmpty } from "lodash";
import { infiniteQueryOptions, queryOptions } from "react-query";

import {
  CHAT_ROOM_MESSAGE_TYPE,
  POST_TYPE,
} from "@/react/types/FlaggedContent";

export const flaggedContentKey = createQueryKeyFactory("flagged-content");

export interface FlaggedContentListPage {
  count: number;
  has_next_page: boolean;
  page: number;
  page_count: number;
  per_page: number;
  records: FlaggedContent[];
}

export type FlaggedContentOrder = Partial<
  Record<"reported_at" | "flagged_contentable_type" | "from", "asc" | "desc">
>;

interface ListParams {
  content_type?: string;
  flagged_by?: string;
  from?: string;
  initialPage?: number;
  order?: FlaggedContentOrder;
  perPage?: number;
  query?: string;
  reason_type?: string;
  status?: string;
}

const contentableParams = (id: string, type: FlaggedContentableType) => {
  if (type === POST_TYPE) return { post_id: id };
  if (type === CHAT_ROOM_MESSAGE_TYPE) return { chat_room_message_id: id };

  return { comment_id: id };
};

export const flaggedContentQueries = {
  list: ({
    from,
    content_type,
    flagged_by,
    order,
    query = "",
    perPage = 20,
    initialPage = 1,
    reason_type,
    status = "inbox",
  }: ListParams = {}) =>
    infiniteQueryOptions({
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      initialPageParam: initialPage,
      queryKey: flaggedContentKey.key("list", {
        content_type,
        flagged_by,
        from,
        initialPage,
        order,
        perPage,
        query,
        reason_type,
        status,
      }),
      queryFn: ({ pageParam = initialPage }: { pageParam?: number }) => {
        const page = pageParam > 0 ? pageParam : 1;

        return reactQueryGet<FlaggedContentListPage>(
          internalApi.flaggedContent.index({
            params: {
              page,
              per_page: perPage,
              status,
              ...(query && { query }),
              ...(from && { from }),
              ...(content_type && { content_type }),
              ...(reason_type && { reason_type }),
              ...(flagged_by && { flagged_by }),
              ...(!isEmpty(order) && { order }),
            },
          }),
        );
      },
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: flaggedContentKey.key("detail", id),
      queryFn: () =>
        reactQueryGet<FlaggedContent>(internalApi.flaggedContent.show({ id })),
    }),
  byContentable: (id: string, type: FlaggedContentableType) =>
    queryOptions({
      queryKey: flaggedContentKey.key("contentable", id, type),
      queryFn: async () => {
        const response = await reactQueryGet<FlaggedContentListPage>(
          internalApi.flaggedContent.index({
            params: contentableParams(id, type),
          }),
        );

        return response.records[0] ?? null;
      },
    }),
};

export const flaggedContentInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: flaggedContentKey.root() }),
};
